import { NativeEmbedCoreFeature } from '@atlassian/native-embeds-core';
import {
  type ClickCaptureBinder,
  type NativeEmbedChildClient,
  maybeParseNativeEmbedParams,
  NativeEmbedChildClient as NativeEmbedChildClientClass,
  SupportedFeatures,
} from '@atlassian/native-embeds-core/child-client';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { nativeEmbedState } from './native-embed-state';

let childClient: NativeEmbedChildClient | null = null;

export function getChildClient(): NativeEmbedChildClient | null {
  return childClient;
}

export function getIsInIframe(): boolean {
  return typeof window !== 'undefined' && window.self !== window.top;
}

/**
 * ARIA container roles where all clicks inside are treated as interactive.
 * Dialogs may contain non-focusable content with no trigger in the same subtree.
 * Listbox/menu/tree are popup containers rendered into detached portals (e.g.
 * Atlaskit PopupSelect), so their trigger's `aria-haspopup` is never an ancestor.
 */
const INTERACTIVE_CONTAINER_ROLES = new Set([
  'dialog',
  'alertdialog',
  'listbox',
  'menu',
  'tree',
]);

/**
 * Returns true if the element (or any ancestor) is interactive, using:
 * - Focusability: `tabIndex >= 0`, `contenteditable`, `<label>`, or containing
 *   a focusable child (catches react-select's non-focusable `__input-container`)
 * - `aria-haspopup`: popup triggers that use `tabIndex=-1` (e.g. select controls)
 * - `data-interactive` / `data-is-text-selectable`: explicit opt-ins
 * - `.menu-portal-target`: DB's react-select portal (outside the trigger subtree)
 * - `INTERACTIVE_CONTAINER_ROLES`: dialog/listbox/menu/tree containers
 */
export function isInteractiveElement(element: Element): boolean {
  // Focusable-child check runs only on the initial target since the ancestor
  // walk goes up, not down — a non-focusable wrapper (e.g. __input-container)
  // would otherwise be missed.
  const hasDirectInput = Array.from(element.children).some(
    (child) => child.tagName === 'INPUT' || child.tagName === 'TEXTAREA'
  );
  if (hasDirectInput) {
    return true;
  }

  let el: Element | null = element;
  while (el) {
    if (el instanceof HTMLElement) {
      if (
        el.tabIndex >= 0 ||
        el.isContentEditable ||
        el.getAttribute('contenteditable') === 'true' ||
        el.tagName === 'LABEL'
      ) {
        return true;
      }
    }
    if (
      el.getAttribute('aria-haspopup') !== null ||
      el.getAttribute('data-interactive') === 'true' ||
      el.getAttribute('data-is-text-selectable') === 'true' ||
      (el instanceof HTMLElement && el.classList.contains('menu-portal-target'))
    ) {
      return true;
    }
    const role = el.getAttribute('role');
    if (role !== null && INTERACTIVE_CONTAINER_ROLES.has(role)) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
}

/**
 * Click capture binder that notifies the host when a click lands on
 * non-interactive whitespace, so that DB interactions (menus, inputs, cells)
 * feel native without triggering embed selection.
 */
export function createSelectiveClickCaptureBinder(): ClickCaptureBinder {
  return {
    bind(handler: () => void) {
      const onClick = (event: MouseEvent): void => {
        if (nativeEmbedState.isSelected) {
          return;
        }
        const target = event.target;
        if (target instanceof Element && isInteractiveElement(target)) {
          return;
        }
        handler();
        if (isFeatureFlagEnabled('platform_native_embeds_full_rollout_bugfix')) {
          nativeEmbedState.isSelected = true;
        }
      };

      document.addEventListener('click', onClick, { capture: true });
      return () => {
        document.removeEventListener('click', onClick, { capture: true });
      };
    },
  };
}

export interface InitBridgeOptions {
  isInIframe?: boolean;
}

export function initBridge(options?: InitBridgeOptions): NativeEmbedChildClient | null {
  const existingClient = getChildClient();
  if (existingClient) {
    return existingClient;
  }

  const inIframe =
    options?.isInIframe !== undefined ? options.isInIframe : getIsInIframe();
  if (!inIframe) {
    return null;
  }

  const parentWindow = window.parent;
  let search: string | undefined;
  try {
    if (parentWindow !== window && parentWindow.location?.search) {
      search = parentWindow.location.search;
    }
  } catch {
    return null;
  }

  if (search === undefined) {
    return null;
  }

  const parsed = maybeParseNativeEmbedParams(new URLSearchParams(search));
  if (parsed === undefined) {
    return null;
  }

  const bridge = getBrowserBridgeAPI();

  const client = new NativeEmbedChildClientClass(
    // @ts-expect-error Yarn PnP creates separate virtual instances of @atlassian/rovo-content-bridge-api
    // for this package vs @atlassian/native-embeds-core, causing a type mismatch despite identical versions
    bridge,
    parsed.childClientId,
    parsed.parentClientId,
    new SupportedFeatures(
      new Set([
        NativeEmbedCoreFeature.ToggleFullScreen,
        NativeEmbedCoreFeature.NotifyHostClick,
        NativeEmbedCoreFeature.SelectedChange,
        NativeEmbedCoreFeature.RequestResize,
        // ViewModeChange lets the host notify us when the inline full-screen
        // preview opens or closes without remounting the iframe (CNS-28621
        // shared-iframe path). On the legacy two-iframe path the iframe is
        // remounted, so the initial nativeEmbedFullscreen query param still
        // covers that case via initStateFromUrl().
        NativeEmbedCoreFeature.ViewModeChange,
        NativeEmbedCoreFeature.NoRefreshFullScreenPreview,
        ...(isFeatureFlagEnabled('platform_native_embeds_full_rollout_bugfix')
          ? (['content-ready'] as const)
          : []),
      ])
    ),
    createSelectiveClickCaptureBinder()
  );

  childClient = client;
  return client;
}

export function resetBridgeForTesting(): void {
  childClient = null;
}

export type { NativeEmbedChildClient };
